import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { VAT_RATE_BY_TREATMENT, VAT_TREATMENTS, type Customer, type InvoiceDetail, type VatTreatment } from '@aviaconsole/shared';
import { capi } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n';
import { useToast } from '../components/Toast';
import { Button, Card, Input, PageHeader, Select, Textarea, vatTreatmentLabel } from '../components/ui';
import { LinesEditor, emptyLine, type EditableLine } from '../components/LinesEditor';

/** Draft editor used for both /invoices/new and draft invoices. */
export function InvoiceEditor({ existing, onSaved }: { existing?: InvoiceDetail; onSaved?: (inv: InvoiceDetail) => void }) {
  const { company } = useAuth();
  const { t, s } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const customers = useQuery({ queryKey: ['customers', company?.id], queryFn: () => capi<Customer[]>('/customers') });
  const [customerId, setCustomerId] = useState(existing?.customerId ?? '');
  const [vat, setVat] = useState<VatTreatment | ''>(existing?.vatTreatment ?? '');
  const [reference, setReference] = useState(existing?.reference ?? '');
  const [subject, setSubject] = useState(existing?.subject ?? '');
  const [paymentDays, setPaymentDays] = useState<string>(existing ? String(existing.paymentDays) : '');
  const [issuedAt, setIssuedAt] = useState(existing?.issuedAt ?? '');
  const [intro, setIntro] = useState(existing?.introText ?? '');
  const [note, setNote] = useState(existing?.noteText ?? '');
  const [lines, setLines] = useState<EditableLine[]>(existing?.lines.map((l) => ({ description: l.description, quantity: l.quantity, unitPriceCents: l.unitPriceCents, vatRate: l.vatRate, discount: l.discount })) ?? [emptyLine()]);

  const customer = customers.data?.find((c) => c.id === customerId);
  const effectiveVat = (vat || customer?.vatTreatment || 'nl_21') as VatTreatment;
  const vatLocked = effectiveVat !== 'nl_21' && effectiveVat !== 'nl_9';

  const save = useMutation({
    mutationFn: () => {
      const body = {
        customerId, reference: reference || undefined, subject: subject || null, paymentDays: paymentDays ? Number(paymentDays) : undefined, issuedAt: issuedAt || undefined,
        vatTreatment: vat || undefined, introText: intro || undefined, noteText: note || undefined,
        lines: lines.map((l) => ({ ...l, vatRate: vatLocked ? VAT_RATE_BY_TREATMENT[effectiveVat] : l.vatRate })),
      };
      return existing ? capi<InvoiceDetail>(`/invoices/${existing.id}`, { method: 'PATCH', json: body }) : capi<InvoiceDetail>('/invoices', { method: 'POST', json: body });
    },
    onSuccess: (inv) => { qc.invalidateQueries({ queryKey: ['invoices'] }); qc.setQueryData(['invoice', company?.id, inv.id], inv); toast.push(t('done')); if (!existing) navigate(`/invoices/${inv.id}`); onSaved?.(inv); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const customerOptions = [{ value: '', label: '—' }, ...(customers.data ?? []).map((c) => ({ value: c.id, label: `${c.name} (${c.number})` }))];
  return (
    <div className="grid two-even">
      <div className="stack">
        <Card>
          <h2>{t('hLines')}</h2>
          <LinesEditor lines={lines} onChange={setLines} vatLocked={vatLocked} vatRate={VAT_RATE_BY_TREATMENT[effectiveVat]} />
        </Card>
        <Card>
          <h2>{t('hInvoiceTexts')}</h2>
          <div className="grid fields" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
            <Textarea label={t('introTop')} value={intro} placeholder={t('emptyIntro')} onChange={(e) => setIntro(e.target.value)} />
            <Textarea label={t('noteBottom')} value={note} placeholder={t('emptyNote')} onChange={(e) => setNote(e.target.value)} />
          </div>
        </Card>
      </div>
      <div className="stack">
        <Card>
          <h2>{t('hDetails')}</h2>
          <div className="stack md">
            <Select label={t('customerLabel')} value={customerId} options={customerOptions} onChange={(e) => setCustomerId(e.target.value)} />
            <Input label={t('subjectLine')} value={subject} onChange={(e) => setSubject(e.target.value)} />
            <Input label={t('reference')} value={reference} onChange={(e) => setReference(e.target.value)} />
            <Select label={t('vatTreatment')} value={vat} options={[{ value: '', label: `${s('Customer')} · ${customer ? s(vatTreatmentLabel(customer.vatTreatment)) : '—'}` }, ...VAT_TREATMENTS.map((v) => ({ value: v, label: s(vatTreatmentLabel(v)) }))]} onChange={(e) => setVat(e.target.value as VatTreatment | '')} />
            <Input label={t('paymentTerms')} type="number" min={0} placeholder={customer ? String(customer.paymentDays) : ''} value={paymentDays} onChange={(e) => setPaymentDays(e.target.value)} />
            <Input label={t('colIssued')} type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} />
            <Button variant="primary" icon="fas fa-check" block disabled={!customerId || lines.length === 0 || save.isPending} onClick={() => save.mutate()}>{existing ? t('saveChanges') : t('create')}</Button>
            {customers.data && customers.data.length === 0 && <div className="small muted">{t('noCustomers')}</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}

export function InvoiceNew() {
  const { t } = useI18n();
  return (
    <>
      <PageHeader crumb={t('invDetailCrumb')} title={t('invAction')} sub={t('invDetailDraftSub')} />
      <InvoiceEditor />
    </>
  );
}
